import React, { Fragment, useMemo } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, Col, Divider, Row } from 'antd';
import Meta from 'antd/lib/card/Meta';
import { Link } from 'react-router-dom';
import { MTag, CurrencyField, MTypography } from 'src/lib';
import { isRegDTypeCOffer } from 'src/utils';

import { RemainingOfferDaysTag } from '../../../../lib/RemainingOfferDaysTag/RemainingOfferDaysTag';
import { Offer } from '../../../../models/offers.models';
import { Color, Image, Spacing } from '../../../../styles';

import * as Styles from './styles';

export interface FeaturedOfferProps {
  featuredOffer: Offer & { hasPendingOfferOrders: boolean };
  setIsWhatsAccreditedInvestorOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const FeaturedOffer = ({ featuredOffer, setIsWhatsAccreditedInvestorOpen }: FeaturedOfferProps) => {
  const coverCard = useMemo(() => {
    return (
      <div className={Styles.featuredOfferMedia}>
        <img
          alt='feature_image'
          className={Styles.featuredOfferImage}
          src={
            featuredOffer?.videoThumbnailUrl ||
            featuredOffer?.assets[0]?.previewImageUrl ||
            featuredOffer?.assets[0]?.url ||
            Image.DefaultOffering
          }
        />
        <Row
          gutter={[
            { xs: 5, sm: 6, md: 8 },
            { xs: 5, sm: 6, md: 8 },
          ]}
          className={Styles.featuredOfferTags}>
          <Col>
            <MTag type='blue'>Featured</MTag>
          </Col>
          <Col>
            <RemainingOfferDaysTag offer={featuredOffer} />
          </Col>
        </Row>
      </div>
    );
  }, [featuredOffer]);

  const metaCard = useMemo(() => {
    return (
      <Fragment>
        <Row>
          <Col span={24}>
            <MTypography
              variant='h4'
              weight='semibold'
              color={Color.Gray10}
              noWrap
              className={Styles.featuredOfferName}>
              {featuredOffer.name}
            </MTypography>
          </Col>
          <Col span={24}>
            <MTypography variant='subtitle1' color={Color.Gray7}>
              {featuredOffer.industry}
            </MTypography>
          </Col>
          <Col span={24}>
            <MTypography variant='subtitle1' color={Color.Gray7}>
              {featuredOffer.securityType}
            </MTypography>
          </Col>
        </Row>
        <Row className={Spacing.mt40}>
          <Col span={24}>
            <MTypography variant='h4' weight='bold' color={Color.Orange6} className={Styles.minimalInvestment}>
              <CurrencyField value={featuredOffer?.minimumInvestCurrencyAmt} />
            </MTypography>
          </Col>

          <Col span={24}>
            <MTypography variant='subtitle1' color={Color.Gray7}>
              Minimum Investment
            </MTypography>
          </Col>
        </Row>
        <Divider className={Styles.divider} />
        <Row
          gutter={[
            { xs: 5, sm: 6, md: 8 },
            { xs: 5, sm: 6, md: 8 },
          ]}>
          {(featuredOffer?.isComingSoon || featuredOffer?.new) && (
            <Col>
              <MTag type='lightblue' closable={false}>
                {featuredOffer?.isComingSoon ? 'Coming Soon' : 'New'}
              </MTag>
            </Col>
          )}

          {featuredOffer?.offerType && (
            <Col>
              <MTag type='gray'>{featuredOffer.offerType}</MTag>
            </Col>
          )}

          {isRegDTypeCOffer(featuredOffer) && (
            <Col>
              <MTag
                type='gray'
                closable={false}
                className={Styles.accreditedInvestorsOnly}
                onClick={event => {
                  event.preventDefault();
                  setIsWhatsAccreditedInvestorOpen(true);
                }}>
                <span className={Styles.accreditedInvestorsOnlyText}>Accredited Investors Only</span>{' '}
                <InfoCircleOutlined />
              </MTag>
            </Col>
          )}
          {featuredOffer?.hasPendingOfferOrders && (
            <Col>
              <MTag type='orange'>Pending Order</MTag>
            </Col>
          )}
        </Row>
      </Fragment>
    );
  }, [featuredOffer]);

  return (
    <Link
      key={featuredOffer?.name}
      to={`/offers/${featuredOffer.shortUrlRoute ? featuredOffer.shortUrlRoute : featuredOffer.id}`}>
      <Card className={Styles.cardWrapper} cover={coverCard}>
        <Meta title={metaCard} />
      </Card>
    </Link>
  );
};

export default FeaturedOffer;
